import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Header from '../Header';
import GameTable from '../GameTable';
import { useAuth } from '../../context/AuthContext';
import { GameWsClient } from '../../services/online/wsClient';
import OnlineBacigaRenderer from '../../game/online/OnlineBacigaRenderer';
import { getAiPersona } from '../../game/online/aiPersonas';
import { toVisualSlot } from '../../game/online/onlineSeat';
import ToastManager from '../../game/ToastManager';
import ScoreMarker from '../../game/ScoreMarker';
import soundVibrationService from '../../services/SoundVibrationService';
import useTeamColors from '../../hooks/useTeamColors';
import useIsMobileDevice from '../../hooks/useIsMobileDevice';
import useLandscapeOrientation from '../../hooks/useLandscapeOrientation';
import { TOAST_ICONS, SEPARATOR, formatTeam, formatPoints } from '../../config/toastIcons';
import BacigaDecisionPanel from './BacigaDecisionPanel';
import WildcardSelector from '../WildcardSelector';
import BacigaLogic from '../../game/BacigaLogic';
import RoundTransitionModal from './RoundTransitionModal';
import MatchWinnerModal from './MatchWinnerModal';
import Phase31SummaryModal from '../Phase31SummaryModal';
import ShufflingModal from '../ShufflingModal';
import { GAME_TIMINGS } from '../../config/gameTimings';
import useRoomChat from '../../hooks/useRoomChat';
import useChatUnread from '../../hooks/useChatUnread';
import ChatPanel from './ChatPanel';
import { EMOTE_PRESETS } from './chatPresets';
import OnlineSettingsModal from './OnlineSettingsModal';

/** A short, stable string summarizing "how many cards does each hand currently have, and does one of them sit covered" — used to decide whether the table needs a rebuild after a GAME_STATE_SYNC (a Las31 hit, a cover/reveal, or a brand new hand) versus one that changed nothing card-shaped (a vote, a declaration). `hasCoveredCard` is included alongside `count` since covering/revealing a card never changes how many cards a hand has — a signature keyed on count alone would silently miss it. */
function handSignature(payload) {
    const others = Object.entries(payload.otherHands)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([seat, info]) => `${seat}:${info.count}:${info.hasCoveredCard ? 1 : 0}`)
        .join(',');
    return `${payload.myHand.length}|${others}`;
}

/** Same team split as offline Baciga.jsx everywhere (seats 0,2 = team1; 1,3 = team2). */
const seatTeam = (seat) => (seat % 2 === 0 ? 1 : 2);

/**
 * Same recap logic as offline Baciga.jsx's buildPhase31Summary, translated
 * from its local playerIdx/declarationsRef/votesRef reads to the seat/
 * decision pairs accumulated online from BACIGA_ACTION_EVENT (see
 * phase31VotingEventsRef). Declarations (baciga/bacigote) always show; a
 * "cartas" vote shows only for a seat not already covered by a declaration;
 * "paso" only shows at all when NOTHING else happened (offline's own
 * fallback), same ordering/dedup rules, just re-derived from a flat event
 * log instead of separate declarations/votes state.
 */
function buildPhase31Summary(rawEvents, playersBySeat) {
    const playerName = (seat) => playersBySeat?.[seat]?.name || `Jugador ${seat + 1}`;
    const events = [];
    for (const { seat, decision } of rawEvents) {
        if (decision === 'baciga' || decision === 'bacigote') {
            events.push({ playerIndex: seat, playerName: playerName(seat), action: decision });
        }
    }
    for (const { seat, decision } of rawEvents) {
        if (decision === 'cartas' && !events.some((e) => e.playerIndex === seat)) {
            events.push({ playerIndex: seat, playerName: playerName(seat), action: 'cartas' });
        }
    }
    if (events.length === 0) {
        for (const { seat, decision } of rawEvents) {
            if (decision === 'paso') {
                events.push({ playerIndex: seat, playerName: playerName(seat), action: 'paso' });
            }
        }
    }
    events.sort((a, b) => a.playerIndex - b.playerIndex);
    return { events };
}

// Pure/stateless (isComodin/isCardBound only read the card object passed in),
// so one shared instance is safe — used only to find/highlight the local
// player's own unbound wildcards, same as offline Baciga.jsx's bacigaLogicRef.
const bacigaLogic = new BacigaLogic();

/**
 * Reconstructs a hand-index-aligned card list (length `count`) for
 * OnlineBacigaRenderer.js's Las31 reveal/reposition calls
 * (revealSeatCards/repositionSeatForLas31/animateDealCardForLas31), which
 * need to know exactly which sprite index each known card belongs to.
 * `realCards` comes in one of two shapes:
 * - mySeat: `myHandRef.current` — every one of my cards, in true order,
 *   covered one included (the server never hides MY OWN hand from me), so
 *   `gapCompacted` is false and nothing needs reconstructing — the covered
 *   index is threaded through separately instead (see revealSeatCards'/
 *   repositionSeatForLas31's own `coveredIndex` argument), since unlike an
 *   opponent's I actually have that card's real value.
 * - an opponent: `payload.otherHands[seat].cards` — already has the covered
 *   card physically removed by the server (bacigaEngine.ts's
 *   getBacigaPlayerView), one entry shorter than `count` — so
 *   `gapCompacted` is true and the gap has to be reinserted at
 *   `coveredIndex` (as `null` — there's genuinely no data for it) before
 *   anything downstream can line the rest up with the real sprites.
 */
function alignLas31Cards(count, coveredIndex, realCards, gapCompacted) {
    if (!gapCompacted) return realCards || [];
    const aligned = new Array(count).fill(null);
    let cursor = 0;
    for (let i = 0; i < count; i++) {
        if (i === coveredIndex) continue;
        aligned[i] = realCards?.[cursor++] ?? null;
    }
    return aligned;
}

/**
 * Offline parity (Baciga.jsx's inline score-message building after a hit or
 * a covered-card reveal, ~2713-2778 and ~3031-3066) — "24", "24 y la
 * tapada", "24 y el comodín"/"y los comodines", "24, la tapada y el/los
 * comodín(es)". Hardcoded Spanish, same precedent as getCardDisplayName in
 * BacigaDecisionPanel.jsx and RondaLogic.getCardValueName — offline never
 * had i18n keys for this either. `cards` is hand-index-aligned (see
 * alignLas31Cards); `coveredIndex` marks the one slot to treat as "la
 * tapada" regardless of whether its value happens to be known (mine always
 * is — the server never hides my own hand from me).
 */
function buildLas31ScoreMessage(cards, coveredIndex) {
    let visibleSum = 0;
    let unboundComodinCount = 0;
    (cards || []).forEach((card, idx) => {
        if (!card || idx === coveredIndex) return;
        if (bacigaLogic.isComodin(card) && !bacigaLogic.isCardBound(card)) {
            unboundComodinCount++;
            return;
        }
        visibleSum += bacigaLogic.getCardValue(card);
    });

    const hasCoveredCard = coveredIndex !== null && coveredIndex !== undefined;
    const hasUnboundComodines = unboundComodinCount > 0;
    const comodinWord = unboundComodinCount === 1 ? 'el comodín' : 'los comodines';

    if (hasCoveredCard && hasUnboundComodines) return `${visibleSum}, la tapada y ${comodinWord}`;
    if (hasCoveredCard) return `${visibleSum} y la tapada`;
    if (hasUnboundComodines) return `${visibleSum} y ${comodinWord}`;
    return `${visibleSum}`;
}

/**
 * The real online Báciga table (RONDA_ONLINE.md Fase 12c), structurally
 * mirroring OnlineRondaScreen.jsx (same ordered-message-queue pattern, same
 * GameWsClient/reconnection wiring) but driven by BacigaPlayerView's very
 * different shape: `pending` (privacy-filtered to this seat already) drives
 * BacigaDecisionPanel/WildcardSelector instead of Ronda's declarationPhase,
 * and BACIGA_ACTION_EVENT (17 possible `kind`s) replaces CARD_PLAYED/
 * RONDA_DECLARATION_EVENT. Unlike Ronda, there is no free card-play — every
 * player input is one of a handful of discrete decisions, so the table
 * itself is never directly interactive; card pickers (which card to hide,
 * which to discard) are plain HTML rows inside BacigaDecisionPanel, not
 * canvas taps.
 */
export default function OnlineBacigaScreen({ onRoundTransition, spectator = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { roomId } = useParams();
    const { authFetch, getAccessToken, user } = useAuth();
    const { teamColors } = useTeamColors();
    const isMobile = useIsMobileDevice();
    // Force landscape orientation - lock orientation while in the game screen
    const { needsRotation } = useLandscapeOrientation(null, { lockOrientation: true });

    const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting | connected | reconnecting
    const [onlineSettingsOpen, setOnlineSettingsOpen] = useState(false);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const [scores, setScores] = useState({ 1: 0, 2: 0 });
    /** Mirrors `scores` for processActionEvent's closure, same reasoning as myHandRef/mySeatRef — mutated synchronously so an immediate points-awarding event (see its own doc comment below) can apply its delta right away without waiting for a state update, then re-synced to the authoritative value on every GAME_STATE_SYNC. */
    const scoresRef = useRef({ 1: 0, 2: 0 });
    /** Header's RetroScoreboard (games won within the match, `rooms.match_score_team1/2` — matchTransition.ts), NOT the current game's 0-18 tally that `scores`/team1Marker/team2Marker track. Seeded from the room fetch in handlePixiMount, kept live by ROUND_TRANSITION/MATCH_OVER's `matchScore` payload — see offline Baciga.jsx's globalMatchScore for the same distinction. */
    const [matchScore, setMatchScore] = useState({ team1: 0, team2: 0 });
    const [fatalError, setFatalError] = useState(null);
    /** The match continues into a fresh round once acked/the deadline passes (matchTransition.ts) — non-null exactly while that hand-off is pending. */
    const [roundTransition, setRoundTransition] = useState(null);
    const [hasAckedTransition, setHasAckedTransition] = useState(false);
    /** The whole match just ended — terminal, no further round to hand off to. */
    const [matchOver, setMatchOver] = useState(null);
    const [pending, setPending] = useState(null);
    const [myHand, setMyHand] = useState([]);
    /** Whether I currently have one of my own Las31 cards hidden from other seats — client-tracked from the event stream (see the class doc comment and BACIGA_ACTION_EVENT handling below); the server never needs to be asked. */
    const [hasCoveredCard, setHasCoveredCard] = useState(false);
    const [processingDecision, setProcessingDecision] = useState(false);
    /** Non-null while the player has voluntarily opened WildcardSelector ahead of the server forcing it — see the "Amarrar Comodín" button below for the full explanation. */
    const [voluntaryWildcardCardIndex, setVoluntaryWildcardCardIndex] = useState(null);
    /** On-table retro score marker (src/game/ScoreMarkerRenderer.js) — same two ScoreMarker instances offline uses, fed from the server's authoritative `scores` on every sync instead of local addPoints() calls. */
    const [team1Marker] = useState(() => new ScoreMarker(1, 'oros', 8));
    const [team2Marker] = useState(() => new ScoreMarker(2, 'oros', 9));
    /** Offline parity (ShufflingModal) — the dealer to show while "shuffling", or null. Offline's own startNewHand() shows this for every round's first hand (handNum === 1); online's equivalent moment is this screen's very first deal, since a fresh mount only ever happens at match start or a round transition. */
    const [shufflingDealer, setShufflingDealer] = useState(null);
    /** Offline parity (Phase31SummaryModal) — non-null exactly while the voting->playing31 recap is blocking the queue; see handleGameStateSync's phase-transition check and phase31SummaryContinueResolveRef. */
    const [phase31SummaryData, setPhase31SummaryData] = useState(null);
    /** Mirrors wsClientRef in state — see OnlineRondaScreen.jsx's identical field for why. */
    const [wsClient, setWsClient] = useState(null);

    const appRef = useRef(null);
    const rendererRef = useRef(null);
    const toastManagerRef = useRef(null);
    const wsClientRef = useRef(null);
    const mySeatRef = useRef(null);
    const playersBySeatRef = useRef(null);
    const hasDealtRef = useRef(false);
    const lastHandSignatureRef = useRef(null);
    /** Header's dealer/turn indicators — see OnlineRondaScreen.jsx's identical fields for why these are refs read straight in render rather than state. */
    const dealerIndexRef = useRef(null);
    const currentTurnRef = useRef(null);
    const pendingResyncRef = useRef(false);
    const queueRef = useRef(Promise.resolve());
    const unmountedRef = useRef(false);
    /** Mirrors the `roundTransition` state for onConnectionChange's closure — see OnlineRondaScreen.jsx's identical field for why a ref, not state, is read there. */
    const roundTransitionRef = useRef(null);
    /** True once ROUND_TRANSITION or MATCH_OVER has been received — see OnlineRondaScreen.jsx's identical field. */
    const expectingTransitionCloseRef = useRef(false);
    /**
     * True from the moment a 'handDealt' BACIGA_ACTION_EVENT arrives until
     * 'las31Started' does — TODO.md (Báciga Las31 timing fix): replaces an
     * earlier approach that diffed `payload.phase` across successive
     * GAME_STATE_SYNCs, which broke once mano is a SYSTEM seat.
     * bacigaGameSession.ts's own class doc comment explains why: a single
     * human-triggered action can cascade through many internal SYSTEM turns
     * — vote, declare, collect counter-declarations, maybe start Las31 —
     * entirely before broadcasting its ONE final GAME_STATE_SYNC (every
     * event in the cascade is still sent individually and in true order,
     * just with no sync in between them). Comparing `phase` between two
     * syncs can't tell which of possibly several transitions inside that
     * gap actually happened; reacting to 'handDealt'/'las31Started'
     * themselves can, since they're emitted in-order alongside everything
     * else in `lastEvents`. Also gates which 'vote' events belong to the
     * voting phase (Las31's own 'planto' is ALSO kind:'vote').
     */
    const inVotingPhaseRef = useRef(false);
    /** {seat, decision} entries collected during the current hand's voting phase — offline parity (Baciga.jsx's declarationsRef/votesRef), consumed by buildPhase31Summary() once voting ends. */
    const phase31VotingEventsRef = useRef([]);
    /** Resolves the Promise handleActionEvent is awaiting on while Phase31SummaryModal is open — same pattern as OnlineRondaScreen.jsx's handSummaryContinueResolveRef. */
    const phase31SummaryContinueResolveRef = useRef(null);
    /** MY OWN hand indices sent along with a 'paso' vote — set in handleVote right before sending, read by the 'cardsPassed' BACIGA_ACTION_EVENT (bacigaEngine.ts never echoes them back, since the actual discard+redeal only happens once every seat has voted). */
    const myDiscardIndicesRef = useRef([]);
    /** MY OWN hand indices whose sprite is still showing a face-down placeholder after animateCardsPassed dealt it, awaiting the real card from the next GAME_STATE_SYNC — consumed by revealReplacementCards in handleGameStateSync. */
    const myPendingRevealIndicesRef = useRef([]);
    /** Mirrors the `myHand` state for handleActionEvent's closure (mounted once in handlePixiMount, so it can never see a later state update directly) — same reasoning as mySeatRef/playersBySeatRef. Kept current in handleGameStateSync right alongside setMyHand. */
    const myHandRef = useRef([]);
    /** Per-seat: which hand INDEX is currently covered during Las31 (or null) — bacigaEngine.ts's events always carry the index itself (never the card), so this is tracked client-side from 'cardToHide'/'dealt31'/'coveredCardRevealed', then used to reconstruct a hand-index-aligned card list for revealSeatCards/repositionSeatForLas31 (see alignLas31Cards). Reset to all-null on every 'handDealt' (Las31 covers never carry over into a new hand). */
    const coveredIndexBySeatRef = useRef({ 0: null, 1: null, 2: null, 3: null });
    /**
     * Per-seat: the hand-index-aligned array of Las31 cards this client
     * actually knows the value of (null at any index it doesn't — the
     * covered one, or occasionally MY OWN new covered card until the next
     * sync). TODO.md ("al empezar el turno... no muestra ninguna carta") —
     * bacigaEngine.ts's 'cardToHide'/'dealt31'/'coveredCardRevealed' events
     * now carry the real value of anything that's already genuinely public
     * (never the covered card itself), so this is populated straight from
     * those events as they arrive and fed back into revealSeatCards/
     * animateDealCardForLas31 immediately — no more waiting for the next
     * GAME_STATE_SYNC to reveal a seat's turn. Seeded from myHandRef for
     * mySeat the moment Las31 starts ('las31Started', below — I always know
     * my own hand); reset to all-empty on every 'handDealt'.
     */
    const knownLas31CardsBySeatRef = useRef({ 0: [], 1: [], 2: [], 3: [] });
    /**
     * Seats whose Las31 hand needs revealSeatCards() run again once the NEXT
     * GAME_STATE_SYNC arrives — narrowed to the two cases that genuinely
     * can't be resolved from the event alone (knownLas31CardsBySeatRef
     * handles everything else immediately): (1) MY OWN new card dealt
     * covered — bacigaEngine.ts's 'dealt31' never includes its value even
     * for me, since the event broadcasts identically to every seat and
     * including it there would leak it to the opponents too; (2) the
     * round-end score-announcement subphase, where endLas31() silently
     * clears every seat's remaining cover with no event of its own, so
     * whatever was still covered only becomes known via that sync's
     * otherHands[seat].cards / myHand.
     */
    const pendingLas31RevealSeatsRef = useRef(new Set());
    /** Subset of pendingLas31RevealSeatsRef that should ALSO get the running Las31 score toast ("24", "24 y la tapada"...) once revealed — only a hit/covered-reveal/Las31 wildcard-bind wants it, not the initial reveal-all-or-except-one (offline doesn't show it there either). */
    const pendingLas31ScoreToastSeatsRef = useRef(new Set());
    /** True once this screen has already shown the very first deal (dealInitial) — a LATER 'handDealt' (a fresh hand within the same round: bacigaEngine.ts's endHand() reshuffles and rotates dealer every hand) sets this so handleGameStateSync's next sync knows to run dealInitial again instead of the usual renderState()/signature diff. */
    const pendingHandRedealRef = useRef(false);
    /** BACIGA_ACTION_EVENTs that arrive while pendingHandRedealRef is true (bacigaEngine.ts can chain the new mano's own AI vote/torito into the SAME burst as the 'handDealt' that announced the new hand, all before the one deferred sync that actually deals it) — held here instead of shown immediately, then replayed in order once handleGameStateSync's dealInitial() finishes, so the human never sees an AI "decide" over an already-cleared, not-yet-dealt table. */
    const pendingRedealEventsRef = useRef([]);
    /** BACIGA_ACTION_EVENTs whose own `seat` still has an outstanding Las31 reveal (i.e. is in pendingLas31RevealSeatsRef) at the moment they'd otherwise be shown — Báciga can chain a whole Las31 turn, a bust, a báciga validation, even the final ligues/score count all in the SAME burst before the one sync that actually reveals an earlier turn's cards, so without this a score/ligues toast (or a second hit) for a seat would show while that seat's hand is still face-down. Drained per seat, in order, by drainPendingSeatEvents() right after that seat's own revealSeatCards() call in handleGameStateSync. */
    const pendingSeatEventsRef = useRef([]);

    /** Same ordering guarantee as OnlineRondaScreen's enqueue — see that file's doc comment. */
    const enqueue = useCallback((fn) => {
        queueRef.current = queueRef.current.then(fn, fn).catch((err) => {
            console.error('[OnlineBacigaScreen] Error processing a queued message:', err);
        });
        return queueRef.current;
    }, []);

    const visualSlotFor = useCallback((seat) => {
        if (seat === undefined || seat === null || mySeatRef.current === null) return 0;
        return toVisualSlot(seat, mySeatRef.current);
    }, []);

    /** {name, avatar} for ShufflingModal — reads playersBySeatRef (populated once in handlePixiMount, before this is ever needed). */
    const toHeaderPlayer = useCallback((seat) => {
        const player = playersBySeatRef.current?.[seat];
        if (!player) return null;
        return { name: player.name, avatar: player.avatar || (player.isSystem ? '🤖' : '🙂') };
    }, []);

    const handleGameStateSync = useCallback((payload, isReconnect = false) => enqueue(async () => {
        if (unmountedRef.current) return;

        // TEMPORARY diagnostic logging (TODO.md: Báciga online dealing/
        // reveal bugs) — remove once those are confirmed fixed. Logs which
        // branch is about to run BEFORE running it, so a copy-pasted
        // console trace shows exactly why a given sync did (or didn't)
        // trigger a deal/redeal/reveal.
        const otherCounts = Object.entries(payload.otherHands ?? {})
            .map(([s, h]) => `${s}:${h.count}${h.hasCoveredCard ? '(c)' : ''}${h.cards ? `[${h.cards.length}]` : ''}`)
            .join(',');
        // TODO.md (Báciga: double shuffling modal) — `pendingHandRedealRef`
        // must be checked ahead of `!hasDealtRef.current` here: a SYSTEM (or
        // human) dealer's torito can still be undecided on this client's
        // very first sync (hasDealtRef stays false — see that branch's own
        // doc comment below), and the 'handDealt' event that resolves it
        // sets `pendingHandRedealRef` BEFORE hasDealtRef ever flips true —
        // checking hasDealtRef first would misroute the real deal's own sync
        // right back into the "first sync" branch instead, animating the
        // shuffle+deal a second time.
        const branch = !hasDealtRef.current && !pendingHandRedealRef.current
            ? 'firstDeal'
            : pendingResyncRef.current
              ? 'resync'
              : pendingHandRedealRef.current
                ? 'handRedeal'
                : payload.phase === 'playing31' || payload.phase === 'roundEnd'
                  ? 'las31-noop'
                  : 'genericDiff';
        console.log(
            `[OnlineBacigaScreen] SYNC branch=${branch} phase=${payload.phase} myHand=${payload.myHand?.length} other=[${otherCounts}] pending=${payload.pending ? `${payload.pending.type}:${payload.pending.seat}` : 'null'}`
        );

        if (!hasDealtRef.current && !pendingHandRedealRef.current) {
            // A spectator has no seat (payload.seat is null) — only a camera
            // angle (payload.viewpointSeat), used purely to orient the table.
            const viewpointSeat = payload.seat ?? payload.viewpointSeat;
            mySeatRef.current = viewpointSeat;
            if (!rendererRef.current) {
                const renderer = new OnlineBacigaRenderer(appRef.current, viewpointSeat, { spectator });
                rendererRef.current = renderer;
                // Offline parity (Baciga.jsx) — without this, mobile always
                // got the fixed 100x150 desktop card size, since this
                // renderer never read the device type on its own.
                const screenWidth = appRef.current.screen.width;
                const cardWidth = isMobile ? Math.floor(screenWidth * 0.06) : (screenWidth < 800 ? 70 : 100);
                const cardHeight = isMobile ? Math.floor(cardWidth * 1.5) : (screenWidth < 800 ? 105 : 150);
                renderer.setCardSize(cardWidth, cardHeight);
                if (playersBySeatRef.current) renderer.setPlayerNames(playersBySeatRef.current);
                renderer.setScoreMarkers(team1Marker, team2Marker);
            }
            // TODO.md (Báciga: double shuffling modal) — with a SYSTEM dealer
            // deferred to resolve after connecting (bacigaSessionRegistry's
            // deferInitialSystemTurn), this very first sync can legitimately
            // arrive BEFORE anyone (human or SYSTEM) has decided torito —
            // `myHand` is still empty, same "not dealt yet" state a human
            // dealer's own torito decision has always produced. Animating a
            // shuffle+deal here for a hand that doesn't exist yet, then
            // AGAIN for the real one once 'handDealt' fires below, was the
            // double-modal bug: skip straight to it only once the table
            // actually has cards; otherwise leave hasDealtRef false so the
            // 'handDealt' event that follows the real deal is the one and
            // only place that triggers the shuffle + deal animation.
            // A spectator never has a hand of their own: "the table is dealt"
            // is read off the seats' card counts instead.
            const tableIsDealt = spectator
                ? Object.values(payload.otherHands ?? {}).some((hand) => hand.count > 0)
                : payload.myHand.length > 0;
            if (tableIsDealt) {
                if (isReconnect) {
                    // TODO.md (leaving/re-entering the app mid-game always
                    // showed "shuffling") — this fresh mount is rejoining a
                    // hand already in progress (the server says so via
                    // GAME_STATE_SYNC's isReconnect), not the true start of a
                    // deal: snap straight to the current state instead of
                    // animating a deal that already happened, possibly hands
                    // ago. Same rebuild-from-scratch path as the
                    // pendingResyncRef branch below, including its Las31
                    // tracking-ref resync — see that branch's own comments.
                    await rendererRef.current.renderState(payload);
                    if (payload.phase === 'playing31' || payload.phase === 'roundEnd') {
                        if (payload.seat !== null) knownLas31CardsBySeatRef.current[payload.seat] = payload.myHand;
                        for (const [seatStr, info] of Object.entries(payload.otherHands ?? {})) {
                            const otherSeat = Number(seatStr);
                            coveredIndexBySeatRef.current[otherSeat] = info.hasCoveredCard ? info.count - 1 : null;
                            knownLas31CardsBySeatRef.current[otherSeat] = info.cards
                                ? alignLas31Cards(info.count, null, info.cards, true)
                                : new Array(info.count).fill(null);
                        }
                    }
                } else {
                    setShufflingDealer(toHeaderPlayer(payload.dealerIndex));
                    await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));
                    setShufflingDealer(null);
                    if (unmountedRef.current) return;
                    await rendererRef.current.dealInitial(payload);
                }
                hasDealtRef.current = true;
                lastHandSignatureRef.current = handSignature(payload);
            }
        } else if (pendingResyncRef.current) {
            // A reconnect always wins over a merely-pending animation flag —
            // a dropped connection could in principle land squarely between
            // 'handDealt' (which sets pendingHandRedealRef) and its own
            // following sync, or between any Las31 event and its reveal —
            // snap straight to whatever the server says is true right now,
            // regardless of how many hands/hits have gone by since.
            // renderState() already branches on payload.phase internally for
            // the right layout (initial hand vs Las31 groups).
            pendingResyncRef.current = false;
            pendingHandRedealRef.current = false;
            // Whatever got buffered (see pendingRedealEventsRef's/
            // pendingSeatEventsRef's own doc comments) is now stale —
            // renderState() below already replaces it with the current
            // truth directly, same "missed it while disconnected" trade-off
            // already accepted elsewhere here. Also drop any outstanding
            // "waiting on a reveal" markers — nothing will ever resolve one
            // left over from before the drop (no more events for it are
            // coming; renderState() already shows the current truth).
            pendingRedealEventsRef.current = [];
            pendingSeatEventsRef.current = [];
            pendingLas31RevealSeatsRef.current.clear();
            pendingLas31ScoreToastSeatsRef.current.clear();
            await rendererRef.current?.renderState(payload);
            // knownLas31CardsBySeatRef/coveredIndexBySeatRef must be
            // resynced here too, not just the sprites — a later 'dealt31'
            // appends onto knownLas31CardsBySeatRef[seat] to build the array
            // revealSeatCards expects, and a stale (pre-reconnect) length
            // would misalign every index from that point on. Same "no way to
            // know MY OWN covered index either" gap renderState() itself
            // already accepts for a reconnect (myHand carries no such
            // marker) — coveredIndexBySeatRef[mySeat] is left as-is here.
            if (payload.phase === 'playing31' || payload.phase === 'roundEnd') {
                if (payload.seat !== null) knownLas31CardsBySeatRef.current[payload.seat] = payload.myHand;
                for (const [seatStr, info] of Object.entries(payload.otherHands ?? {})) {
                    const otherSeat = Number(seatStr);
                    coveredIndexBySeatRef.current[otherSeat] = info.hasCoveredCard ? info.count - 1 : null;
                    knownLas31CardsBySeatRef.current[otherSeat] = info.cards
                        ? alignLas31Cards(info.count, null, info.cards, true)
                        : new Array(info.count).fill(null);
                }
            }
            lastHandSignatureRef.current = handSignature(payload);
        } else if (pendingHandRedealRef.current) {
            // TODO.md (Báciga redeal parity) — bacigaEngine.ts's endHand()
            // reshuffles the whole deck and rotates the dealer on EVERY hand
            // within the round (unlike Ronda's same-deck internal re-deals),
            // so a fresh hand deserves the same full animated deal as the
            // very first one, not an instant snap. 'handDealt' (handled in
            // handleActionEvent below) already showed the shuffling beat and
            // cleared the table; this sync is the first one carrying the
            // new hand's real cards to actually deal out.
            pendingHandRedealRef.current = false;
            await rendererRef.current?.dealInitial(payload);
            // TODO.md (Báciga: double shuffling modal) — a no-op when the
            // very first sync already had a dealt hand (hasDealtRef is
            // already true by then); needed when it didn't, so a dealer's
            // deferred torito decision counts as this client's real first
            // deal exactly once, here, and not a second time.
            hasDealtRef.current = true;
            lastHandSignatureRef.current = handSignature(payload);
            // Replay, in order, whatever arrived while the redeal was
            // pending (pendingRedealEventsRef's own doc comment) — now that
            // the new hand is actually visible, it's safe to show the new
            // mano's own AI vote/torito toast (if any) exactly as it would
            // have looked had it arrived just now instead of mid-shuffle.
            const bufferedRedealEvents = pendingRedealEventsRef.current;
            pendingRedealEventsRef.current = [];
            for (const bufferedPayload of bufferedRedealEvents) {
                await processActionEvent(bufferedPayload);
            }
        } else if (payload.phase === 'playing31' || payload.phase === 'roundEnd') {
            // TODO.md (Báciga online Las31: cards going back face-down) —
            // 'roundEnd' (bacigaEngine.ts's endLas31()) is the ligues/score
            // announcement subphase, NOT a new hand — everyone's Las31
            // reveal state (otherHands[seat].cards, coverState) is still
            // exactly as meaningful here as during 'playing31' itself. This
            // used to check only 'playing31', so the very first sync after
            // the LAST seat stands/busts (now carrying phase:'roundEnd')
            // fell through to the generic branch below instead — whose
            // renderState() computes `isLas31 = phase === 'playing31'`,
            // now false, so it rebuilt EVERY opponent's hand as an ordinary
            // (non-Las31) face-down hand, discarding every reveal that had
            // already happened. From the human's side that looked exactly
            // like cards flipping back face-down the instant their own turn
            // ended (mano's turn is usually the one right before the
            // subphase change) — then getting properly re-revealed, grouped,
            // and animated moments later by the SAME 'scoreAnnounced'-driven
            // drainLas31Reveals() path that already handles this correctly,
            // masking the real cause. Every card-shaped change in EITHER
            // subphase already has its own dedicated animation triggered
            // straight off its own BACIGA_ACTION_EVENT
            // (animateDealCardForLas31/revealSeatCards/drainLas31Reveals) —
            // handSignature's count/hasCoveredCard diff would otherwise see
            // the exact same change and instantly snap everything into place
            // right on top of that animation, in both subphases alike.
            // lastHandSignatureRef is still kept current so leaving Las31
            // entirely (the next hand's 'dealing'/'voting') is correctly
            // detected as a change again.
            lastHandSignatureRef.current = handSignature(payload);
        } else {
            const sig = handSignature(payload);
            if (sig !== lastHandSignatureRef.current) {
                await rendererRef.current?.renderState(payload);
                lastHandSignatureRef.current = sig;
            }
        }

        dealerIndexRef.current = payload.dealerIndex;
        currentTurnRef.current = payload.pendingSeat;
        setScores(payload.scores);
        scoresRef.current = { ...payload.scores };
        setPending(payload.pending);
        setMyHand(payload.myHand);
        myHandRef.current = payload.myHand;
        if (myPendingRevealIndicesRef.current.length > 0) {
            // The 'cardsPassed' animation dealt these face-down since it
            // never knows a replacement's real identity — this sync is the
            // first place that identity is actually available.
            const revealIndices = myPendingRevealIndicesRef.current;
            myPendingRevealIndicesRef.current = [];
            await rendererRef.current?.revealReplacementCards(revealIndices, payload.myHand);
        }
        // Same reasoning as myPendingRevealIndicesRef above, per Las31 seat
        // instead of per my-own-hand-index: 'cardToHide'/'dealt31'/
        // 'coveredCardRevealed' announce that a reveal is due, this sync is
        // the first place with real data to reveal it from. Also replays
        // whatever handleActionEvent buffered while any seat's reveal was
        // outstanding — see drainLas31Reveals' own doc comment.
        await drainLas31Reveals(payload);
        setProcessingDecision(false);
        team1Marker.setScore(payload.scores[1]);
        team2Marker.setScore(payload.scores[2]);
        rendererRef.current?.updateScoreMarkers();
        await rendererRef.current?.updateDeck(payload.dealerIndex, payload.deckRemaining);

        if (payload.roundOver) {
            // Terminal for this ROUND, not necessarily the match — a
            // ROUND_TRANSITION (continue into a fresh round, possibly Ronda
            // under CLASSIC mode) or MATCH_OVER event follows shortly after
            // (matchTransition.ts), handled by their own listeners below.
            rendererRef.current?.disablePlayerCardsInteractivity(mySeatRef.current);
        }
    }), [enqueue, team1Marker, team2Marker, toHeaderPlayer, processActionEvent, drainLas31Reveals, isMobile, spectator]);

    /** Offline parity (Baciga.jsx's running Las31 score recap — "24", "24 y la tapada", "24 y el comodín"...) — shared by processActionEvent's immediate-reveal path (a hit/covered-reveal whose value is already known from the event itself) and drainLas31Reveals' still-sync-dependent one (my own covered hit, the round-end reveal). */
    const showLas31ScoreRecap = useCallback((seat, alignedCards, coveredIndex) => {
        const teamColor = seatTeam(seat) === 1 ? teamColors.team1.hex : teamColors.team2.hex;
        toastManagerRef.current?.showPlayerToast(buildLas31ScoreMessage(alignedCards, coveredIndex), visualSlotFor(seat), {
            type: 'team' + seatTeam(seat), teamColor, duration: 2000,
        });
    }, [teamColors, visualSlotFor]);

    const processActionEvent = useCallback(async (payload) => {
        if (unmountedRef.current) return;
        const { kind, seat, pointsAwarded, awardedTeam } = payload;
        // A spectator owns no seat: null never equals a real seat, so every
        // "is this MY seat" branch below treats all four (seat 0 included, which
        // is only the camera angle) as an opponent whose cards come from the event.
        const mySeat = spectator ? null : mySeatRef.current;

        // TEMPORARY diagnostic logging (see handleGameStateSync's own log
        // for why) — every BACIGA_ACTION_EVENT as the client sees it, before
        // any AI-thinking pause or state mutation below.
        console.log(`[OnlineBacigaScreen] EVENT kind=${kind} seat=${seat} payload=${JSON.stringify(payload)}`);

        // TODO.md ("canté bacigote y no se apuntaron los 7 puntos") — this
        // used to run AFTER the whole switch below, which for a
        // points-awarding event that also reveals cards (bacigoteWin/
        // bacigaInstantWin/lasCosas) meant it ran after an `await
        // revealSeatCards(...)` that loads real card textures for the first
        // time (bound wildcards included). If that promise ever rejected —
        // a slow/failed asset load is enough — the exception unwound this
        // whole async function, enqueue()'s own catch swallowed it, and the
        // score delta below NEVER ran: the toast (which fires earlier, so
        // it survived) announced the win while the marker silently stayed
        // put. `pointsAwarded`/`awardedTeam` are already known from the raw
        // payload here, before any of that can throw, so applying the delta
        // THIS EARLY makes it immune to anything the rest of this function
        // does later — a real GAME_STATE_SYNC still resyncs scoresRef to the
        // authoritative value regardless (handleGameStateSync), so this is
        // belt-and-suspenders, not the only source of truth. `bust`'s own
        // penalty is negative, so this checks for ANY non-zero delta, not
        // just a positive one.
        if (pointsAwarded && awardedTeam) {
            scoresRef.current = { ...scoresRef.current, [awardedTeam]: scoresRef.current[awardedTeam] + pointsAwarded };
            setScores({ ...scoresRef.current });
            team1Marker.setScore(scoresRef.current[1]);
            team2Marker.setScore(scoresRef.current[2]);
            rendererRef.current?.updateScoreMarkers();
        }

        // Offline parity (GAME_TIMINGS.ai.thinkingTime) — same fix as
        // OnlineRondaScreen.jsx's applyCardPlayed/handleDeclarationEvent: the
        // server resolves a whole chain of SYSTEM decisions instantly, so
        // without this beat every AI vote/torito/Las31 action would flash by
        // at once. Delaying here also delays the queue's next task — this
        // event's own GAME_STATE_SYNC, which is what flips `pending` to the
        // next actor — so turn changes visibly wait too, not just the toast.
        if (seat !== undefined && seat !== mySeat && playersBySeatRef.current?.[seat]?.isSystem) {
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.ai.thinkingTime));
        }
        // TODO.md ("el conteo de ligues... sobre todo la del ultimo jugador
        // en hacer el conteo. Debe esperar un instante tras el ultimo
        // jugador antes de pasar al siguiente") — bacigaEngine.ts's
        // stepRoundEnd() emits one 'scoreAnnounced' (+'liguesWin') per seat
        // back-to-back, then 'handEnded' right after the last one in the
        // SAME burst. The pause above only ever covered a SYSTEM seat's own
        // turn, so whenever the LAST seat to announce was human (no pause in
        // front of ITS toast either), 'handEnded' — and shortly after, the
        // next hand's own deal — landed on top of that toast before it had
        // any real time on screen. This is unconditional (not itself gated
        // on the last seat being human) since the scoring cascade's own
        // pacing already gives every OTHER seat's toast a beat to be read
        // before the next one arrives, whether from this same pause (system)
        // or the real turn-by-turn timing of a human decision — 'handEnded'
        // was the one moment nothing paced.
        if (kind === 'handEnded') {
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.bacigaOnlineCounting.afterLastScoreAnnouncement));
        }
        if (unmountedRef.current) return;

        const slot = visualSlotFor(seat);
        // Offline parity (Baciga.jsx tints every per-player toast with that
        // player's own team color, e.g. safeShowPlayerToast(..., { teamColor:
        // playerTeamColor })) — the real seat's team, not the visually
        // rotated `slot` used only for on-screen placement.
        const teamColor = seatTeam(seat) === 1 ? teamColors.team1.hex : teamColors.team2.hex;
        // Icon+color for a points-worthy event: the awarded team when there
        // is one (bust's penalty can go to the busting player's own team),
        // otherwise the acting seat's team.
        const awardedTeamColor = awardedTeam === 1 ? teamColors.team1.hex : awardedTeam === 2 ? teamColors.team2.hex : teamColor;

        switch (kind) {
            case 'torito':
                // Offline parity (Baciga.jsx's startNewHand: only the AI-dealer
                // case toasts, and only when torito is actually used).
                if (payload.wantsTorito) {
                    toastManagerRef.current?.showPlayerToast(`🎯 ${t('online.play.baciga.event.torito_yes')}`, slot, {
                        type: 'team' + seatTeam(seat), teamColor, duration: 2000,
                    });
                }
                break;
            case 'handDealt': {
                // TODO.md (Báciga Las31 timing fix) — the reliable, burst-
                // proof "a fresh hand's voting phase has begun" signal (see
                // inVotingPhaseRef's own doc comment for why syncs alone
                // can't do this once mano is a SYSTEM seat). Also doubles as
                // the trigger for a full redeal animation on every hand
                // within the round, not just the very first one:
                // bacigaEngine.ts's endHand() reshuffles the whole deck and
                // rotates the dealer on EVERY hand (unlike Ronda's same-deck
                // internal re-deals), so each one deserves the same
                // animated deal as the first, not an instant snap.
                inVotingPhaseRef.current = true;
                phase31VotingEventsRef.current = [];
                coveredIndexBySeatRef.current = { 0: null, 1: null, 2: null, 3: null };
                knownLas31CardsBySeatRef.current = { 0: [], 1: [], 2: [], 3: [] };
                pendingLas31RevealSeatsRef.current.clear();
                pendingLas31ScoreToastSeatsRef.current.clear();
                // TODO.md (Báciga: double shuffling modal) — no longer gated
                // behind `hasDealtRef.current`: that used to distinguish "the
                // very first hand" (handled by handleGameStateSync's own
                // first-sync branch instead) from "a later one," but that
                // first-sync branch now skips its own shuffle+deal entirely
                // whenever the table isn't actually dealt yet (a deferred
                // SYSTEM dealer's torito, or a human dealer's own pending
                // torito) — so by the time this event ever fires, it's
                // always a genuinely new deal this client hasn't rendered,
                // whether that's the deferred first one or an ordinary
                // mid-round redeal. Firing this unconditionally is what makes
                // the shuffle modal appear exactly once either way.
                setShufflingDealer(toHeaderPlayer(payload.dealerIndex));
                await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));
                setShufflingDealer(null);
                if (unmountedRef.current) return;
                rendererRef.current?.clearAllCards();
                pendingHandRedealRef.current = true;
                break;
            }
            case 'las31Started': {
                // TODO.md (Báciga Las31 timing fix) — everything that used to
                // live in handleGameStateSync's old phase-diffing block moves
                // here verbatim, reading myHandRef instead of a sync payload
                // (there is no GAME_STATE_SYNC at this exact point — this is
                // a BACIGA_ACTION_EVENT, emitted in true order alongside
                // every vote/declaration that led here).
                inVotingPhaseRef.current = false;
                const summaryEvents = phase31VotingEventsRef.current;
                phase31VotingEventsRef.current = [];
                // A báciga/bacigote/cartas declaration can preempt an
                // already-sent 'paso' vote: bacigaEngine.ts's applyVote()
                // clears pendingDiscards without ever calling
                // dealReplacements(), so the 'cardsPassed' event that would
                // normally consume myDiscardIndicesRef (and reveal the
                // batch) never arrives — the cards I'd already flipped
                // face-down as a discard preview are stuck that way unless
                // reverted here.
                if (myDiscardIndicesRef.current.length > 0) {
                    const abandonedIndices = myDiscardIndicesRef.current;
                    myDiscardIndicesRef.current = [];
                    await Promise.all(abandonedIndices.map((idx) => rendererRef.current?.revertDiscardPreview(idx)));
                }
                await new Promise((resolve) => {
                    phase31SummaryContinueResolveRef.current = resolve;
                    setPhase31SummaryData(buildPhase31Summary(summaryEvents, playersBySeatRef.current));
                });
                if (unmountedRef.current) return;
                // Offline parity (Baciga.jsx's start31Phase: sweeps the
                // voting phase's discard pile into the deck right after the
                // summary modal closes, before Las31 itself starts) —
                // bacigaEngine.ts's startLas31() moves the same cards to the
                // bottom of the deck logically at this exact transition.
                await rendererRef.current?.animateCenterCardsToDeck();
                // Offline parity (Baciga.jsx's start31Phase ->
                // animateAllToLas31, right after the sweep above) — only my
                // own cards are actually known yet (myHandRef.current);
                // every opponent still spreads ungrouped until their own
                // reveal (below) repositions them.
                knownLas31CardsBySeatRef.current[mySeat] = myHandRef.current;
                await rendererRef.current?.animateAllSeatsToLas31(myHandRef.current);
                break;
            }
            case 'vote':
            case 'counterDeclare': {
                const voteIcons = { paso: TOAST_ICONS.PASO, baciga: TOAST_ICONS.BACIGA, bacigote: TOAST_ICONS.BACIGOTE };
                const icon = voteIcons[payload.decision];
                toastManagerRef.current?.showPlayerToast(
                    `${icon ? icon + ' ' : ''}${t(`online.play.baciga.vote_option.${payload.decision}`)}`,
                    slot,
                    { type: 'team' + seatTeam(seat), teamColor, duration: 2000 }
                );
                // Offline parity (Baciga.jsx's buildPhase31Summary) — collect
                // this voting-phase decision so Phase31SummaryModal can
                // recap it once voting ends ('las31Started', below). Las31's
                // own 'planto' is ALSO kind:'vote' (bacigaEngine.ts's
                // applyLas31Action), so this only accumulates while actually
                // in the voting phase.
                if (inVotingPhaseRef.current) {
                    phase31VotingEventsRef.current.push({ seat, decision: payload.decision });
                }
                break;
            }
            case 'wildcardBound':
                if (seat !== mySeat) {
                    toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.WILDCARD} ${t('online.play.baciga.event.wildcardBound')}`, slot, {
                        type: 'team' + seatTeam(seat), teamColor, duration: 1800,
                    });
                }
                // Offline parity — binding a wildcard during Las31 changes
                // the running total and can change which cards visually
                // stack together (ligues), so it gets the same reveal+
                // reposition treatment (revealSeatCards re-groups even an
                // already-fully-revealed hand harmlessly) and score-recap
                // toast as a hit/covered-reveal. Never during voting, where
                // a bind is just ahead of a báciga/bacigote declaration and
                // there's no Las31 "score so far" to speak of yet.
                if (!inVotingPhaseRef.current) {
                    pendingLas31RevealSeatsRef.current.add(seat);
                    pendingLas31ScoreToastSeatsRef.current.add(seat);
                }
                break;
            case 'cardToHide': {
                // Offline parity (revealCards/revealCardsExceptOne) — the
                // very start of `seat`'s Las31 turn: reveal everything
                // except `cardIndex` (null = reveal all). TODO.md ("al
                // empezar el turno... no muestra ninguna carta") —
                // bacigaEngine.ts now includes `revealedCards` (every card
                // except the covered one, hand-index-aligned) directly in
                // this event, so an opponent's turn reveals right here,
                // immediately, instead of waiting for the next
                // GAME_STATE_SYNC — which could be an entire multi-seat
                // burst away.
                coveredIndexBySeatRef.current[seat] = payload.cardIndex;
                const hidingSomething = payload.cardIndex !== null && payload.cardIndex !== undefined;
                if (seat === mySeat) {
                    setHasCoveredCard(hidingSomething);
                    knownLas31CardsBySeatRef.current[seat] = myHandRef.current;
                    await rendererRef.current?.revealSeatCards(seat, myHandRef.current, payload.cardIndex);
                } else {
                    // Offline parity — Baciga.jsx's revealCards()/
                    // revealCardsExceptOne() (the exact equivalent of this
                    // reveal step) never show any message at all, for either
                    // case; only actually hiding a card gets a toast here
                    // (worth knowing a covered card is now in play), not the
                    // far more common "revealed everything" case.
                    if (hidingSomething) {
                        toastManagerRef.current?.showPlayerToast(
                            t('online.play.baciga.event.hidCard'),
                            slot,
                            { type: 'team' + seatTeam(seat), teamColor, duration: 1800 }
                        );
                    }
                    knownLas31CardsBySeatRef.current[seat] = payload.revealedCards;
                    await rendererRef.current?.revealSeatCards(seat, payload.revealedCards, payload.cardIndex);
                }
                break;
            }
            case 'dealt31': {
                // A hit — deal one face-down placeholder from the deck into
                // that seat's Las31 spread now (its real value, if already
                // public, travels separately in `payload.card` below —
                // bacigaEngine.ts's event never puts it directly on the
                // placeholder step, since the placeholder is identical
                // whether the card ends up covered or not).
                if (payload.covered) {
                    coveredIndexBySeatRef.current[seat] = rendererRef.current?.getPlayerCardSprites(seat).length ?? null;
                    // Bug fix (TODO.md): this was never set for a card dealt
                    // covered mid-Las31 (only the INITIAL reveal-except-one
                    // at 'cardToHide' set it) — without it, BacigaDecisionPanel
                    // never shows the "revelar carta" button for a cover
                    // picked up on a later hit, even though I can always see
                    // my own card and am allowed to reveal it any time.
                    if (seat === mySeat) setHasCoveredCard(true);
                }
                const existingAligned = knownLas31CardsBySeatRef.current[seat];
                await rendererRef.current?.animateDealCardForLas31(seat, existingAligned, coveredIndexBySeatRef.current[seat]);
                // No "pide carta" toast here (dropped per user feedback —
                // it overlapped everything else on screen); the running
                // score recap below already tells the same story ("9 y la
                // tapada" implies a card was just requested).
                if (payload.covered) {
                    knownLas31CardsBySeatRef.current[seat] = [...existingAligned, null];
                    // TODO.md ("al empezar el turno... no muestra ninguna
                    // carta") — an opponent's covered card is genuinely
                    // secret (nothing more to do here; it stays that way
                    // until coveredCardRevealed or the round-end reveal).
                    // MY OWN covered card is the one case bacigaEngine.ts's
                    // event still can't safely carry a value for — it
                    // broadcasts identically to every seat, so including it
                    // here would leak it to the opponents too. Still needs
                    // the next sync (payload.myHand).
                    if (seat === mySeat) {
                        pendingLas31RevealSeatsRef.current.add(seat);
                        pendingLas31ScoreToastSeatsRef.current.add(seat);
                    }
                } else {
                    // Already public — the event carries its real value, so
                    // reveal and recap the running score right now instead
                    // of waiting for a sync that might be an entire
                    // multi-seat burst away.
                    knownLas31CardsBySeatRef.current[seat] = [...existingAligned, payload.card];
                    await rendererRef.current?.revealSeatCards(seat, knownLas31CardsBySeatRef.current[seat], coveredIndexBySeatRef.current[seat]);
                    showLas31ScoreRecap(seat, knownLas31CardsBySeatRef.current[seat], coveredIndexBySeatRef.current[seat]);
                }
                break;
            }
            case 'coveredCardRevealed': {
                coveredIndexBySeatRef.current[seat] = null;
                if (seat === mySeat) setHasCoveredCard(false);
                // No announcement toast here (dropped per user feedback —
                // just the running score recap below, "9 y la tapada" style,
                // same as any other hit). The card was already dealt
                // earlier — this only lifts the cover — and the event now
                // carries its real value directly (`payload.card`, safe
                // since it's genuinely public from this point on), so this
                // resolves immediately instead of waiting for a sync.
                const revealed = knownLas31CardsBySeatRef.current[seat].map((c, idx) =>
                    idx === payload.cardIndex ? payload.card : c
                );
                knownLas31CardsBySeatRef.current[seat] = revealed;
                await rendererRef.current?.revealSeatCards(seat, revealed, null);
                showLas31ScoreRecap(seat, revealed, null);
                break;
            }
            case 'scoreAnnounced': {
                // Offline parity (Baciga.jsx's processPlayerScoreAnnouncement:
                // "Tengo N puntos" / "Me he pasado, no me apunto nada") — the
                // per-seat announcement bacigaEngine.ts's stepRoundEnd() walks
                // through in turn order (scoreOrder, starting at mano), one
                // seat at a time; a 'liguesWin' event (if any) follows right
                // after for the same seat, and 'handEndWinner' closes out the
                // whole sequence once every seat has announced.
                if (payload.busted) {
                    toastManagerRef.current?.showPlayerToast(
                        `${TOAST_ICONS.WARNING} ${t('online.play.baciga.event.scoreBusted')}`,
                        slot,
                        { type: 'error', teamColor, duration: 2000 }
                    );
                } else {
                    toastManagerRef.current?.showPlayerToast(
                        t('online.play.baciga.event.scorePoints', { points: payload.points }),
                        slot,
                        { type: 'team' + seatTeam(seat), teamColor, duration: 2000 }
                    );
                }
                // bacigaEngine.ts's endLas31() clears every seat's cover
                // state in one shot with no event of its own — this is the
                // first per-seat signal afterward, so it doubles as the cue
                // to finally reveal a card that stayed covered all through
                // Las31 (offline's own revealCards call at the top of
                // processPlayerScoreAnnouncement) and to re-group ligues now
                // that the whole hand is settled.
                if (coveredIndexBySeatRef.current[seat] !== null) {
                    coveredIndexBySeatRef.current[seat] = null;
                    if (seat === mySeat) setHasCoveredCard(false);
                }
                // TODO.md ("se pasa a la fase de reparto sin terminar los
                // turnos de conteo de ligues") — this event now carries the
                // seat's ENTIRE final hand directly (every card is
                // genuinely public by round-end), so it reveals immediately
                // instead of waiting for a GAME_STATE_SYNC. That wait used
                // to be the actual bug: bacigaEngine.ts's endHand() can
                // chain straight into the NEXT hand's own deal within this
                // SAME action's cascade, so the one deferred sync could
                // already reflect that next hand's cards by the time it
                // arrived — revealing the WRONG hand mid-announcement,
                // which looked exactly like cards vanishing before every
                // seat had finished their score announcement.
                knownLas31CardsBySeatRef.current[seat] = payload.cards;
                await rendererRef.current?.revealSeatCards(seat, payload.cards, null);
                break;
            }
            case 'bust':
                if (seat === mySeat) setHasCoveredCard(false);
                // Offline parity (Baciga.jsx's "¡PASADO!" toasts: type 'error', team-colored).
                toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.WARNING} ${t('online.play.baciga.event.bust')}`, slot, { type: 'error', teamColor, duration: 2200 });
                soundVibrationService.triggerCaida();
                break;
            case 'bacigoteWin':
            case 'bacigaInstantWin':
            case 'bacigaValidated':
            case 'bacigaInvalidated':
            case 'liguesWin':
            case 'lasCosas':
            case 'rivalBustBonus': {
                // Offline parity (Baciga.jsx's per-event icons: BACIGOTE 💥,
                // BACIGA 🎴, ACCEPT/REJECT for a validated/invalidated báciga,
                // PAIRS 💢 for ligues, POINTS ⭐ for the rival-bust bonus —
                // "Las Cosas" itself carries no icon offline either, just
                // the team color).
                const eventIcons = {
                    bacigoteWin: TOAST_ICONS.BACIGOTE,
                    bacigaInstantWin: TOAST_ICONS.BACIGA,
                    bacigaValidated: TOAST_ICONS.ACCEPT,
                    bacigaInvalidated: TOAST_ICONS.REJECT,
                    liguesWin: TOAST_ICONS.PAIRS,
                    rivalBustBonus: TOAST_ICONS.POINTS,
                };
                const icon = eventIcons[kind];
                // TODO.md ("indicar con un mensaje tengo ligues y los puntos
                // que se apunta") — `points` is only actually referenced by
                // liguesWin's own translation string; passing it for every
                // kind here is a harmless no-op for the others (i18next
                // ignores unused interpolation vars).
                toastManagerRef.current?.showPlayerToast(
                    `${icon ? icon + ' ' : ''}${t(`online.play.baciga.event.${kind}`, { points: pointsAwarded })}`,
                    slot,
                    { type: 'team' + seatTeam(seat), teamColor: awardedTeamColor, duration: 2200 }
                );
                // TODO.md ("cuando hay bacigote, debería el jugador revelar
                // sus cartas, para comprobarlo el resto de jugadores" / "en
                // báciga online, cuando un jugador se haga las cosas debería
                // mostrar todas sus cartas... y esperar unos segundos") —
                // Baciga.jsx's own resolveDeclarations equivalent reveals
                // the declaring seat's hand right after this same toast, for
                // everyone to verify the claim. bacigoteWin/bacigaInstantWin/
                // lasCosas all carry `cards` (each one ends the hand or round
                // outright, so the wildcards involved are already bound and
                // nothing is left to keep secret); the rest of this shared
                // block has nothing to reveal. The real "wait a bit" the
                // user asked for is the server's own pacing pause — either
                // bacigaEngine.ts's awaitingHandTransition (mid-round) or, if
                // this event also ends the ROUND (e.g. las cosas hitting 18),
                // bacigaGameSession.ts's roundOverDelayMs, which holds off
                // the ROUND_TRANSITION broadcast that would otherwise swap
                // this toast+reveal for the winner modal — nothing further
                // to fake here.
                if (payload.cards) {
                    knownLas31CardsBySeatRef.current[seat] = payload.cards;
                    try {
                        await rendererRef.current?.revealSeatCards(seat, payload.cards, null);
                    } catch (err) {
                        // TODO.md ("canté bacigote y no se apuntaron los 7
                        // puntos") — a texture-load failure here (a bound
                        // comodín is exactly the riskiest case) used to throw
                        // out of this whole async function; the score delta
                        // above already ran by now regardless, but every
                        // OTHER case in this shared block (and this seat's
                        // own reveal) still deserves a chance to run rather
                        // than being silently swallowed by enqueue()'s catch.
                        console.error('[OnlineBacigaScreen] revealSeatCards failed for', kind, 'seat', seat, err);
                    }
                }
                break;
            }
            case 'handEndWinner': {
                // TODO.md ("quitar el mensaje de ganó la mano... mostrar los
                // puntos que se anoten sobre el jugador... mismo formato que
                // la báciga offline") — the old generic "Gana la mano" text
                // named neither the win type nor the points awarded, unlike
                // Baciga.jsx's end31Phase ("¡Nombre gana con Las 31! (+2)" /
                // "¡Nombre gana el Punto con N! (+1)"). The player-toast
                // already appears above `seat` — same stand-in for offline's
                // "¡Nombre..." every other event in this phase already
                // relies on — so only the win type + points need to survive
                // translation. `handScore` (the seat's actual hand total,
                // e.g. 24) only matters for 'elPunto' — a 'las31' win is
                // always exactly 31, same as offline never bothering to
                // show it there either.
                const text = payload.handEndType === 'las31'
                    ? t('online.play.baciga.event.handEndWinnerLas31')
                    : t('online.play.baciga.event.handEndWinnerElPunto', { score: payload.handScore });
                toastManagerRef.current?.showPlayerToast(
                    `${TOAST_ICONS.WINNER} ${text}`,
                    slot,
                    { type: 'team' + seatTeam(seat), teamColor: awardedTeamColor, duration: 2200 }
                );
                break;
            }
            case 'handEnded':
                setHasCoveredCard(false);
                toastManagerRef.current?.showToast(t('online.play.baciga.event.handEnded'), { type: 'info', duration: 2000, position: 'center' });
                // TODO.md ("añade un tiempo de espera controlado por el
                // servidor antes de... pasar a repartir de nuevo") — this
                // used to fake the pause itself with a flat client-side
                // setTimeout (offline's own approach, since offline has no
                // server to defer to). Online now has one: bacigaEngine.ts's
                // endHand() pauses for real (awaitingHandTransition) and
                // bacigaGameSession.ts's own timer is what resumes it, so
                // the next hand's events simply don't arrive over the wire
                // until that real pause elapses — nothing left to fake here.
                break;
            case 'cardsPassed': {
                // TODO.md (card-pass animation parity) — everyone finished
                // voting 'paso' in the same cycle, bacigaEngine.ts's
                // dealReplacements() just discarded+redealt in one silent
                // batch. No `seat` on this event (it's table-wide, not one
                // seat's turn), so the AI-thinking pause above never delayed
                // it — correct, each seat's own 'paso' vote was already
                // paced individually. myDiscardIndicesRef is what I sent
                // when *I* voted paso (never echoed back by the server);
                // handed off to myPendingRevealIndicesRef so the next
                // GAME_STATE_SYNC (which is what actually carries my new
                // cards) can reveal them via revealReplacementCards.
                const myIndices = myDiscardIndicesRef.current;
                myDiscardIndicesRef.current = [];
                await rendererRef.current?.animateCardsPassed(payload.discardCounts, myIndices, dealerIndexRef.current);
                myPendingRevealIndicesRef.current = myIndices;
                break;
            }
            default:
                break;
        }

        // Offline parity (Ronda.jsx's awardRondaPoints/Baciga's generic point
        // awards) — a plain team-score banner, never the Ronda-only "Envío"
        // wording this used to reuse from online.play.declaration.resolved.
        // TODO.md ("en la fase de conteo de ligues... se muestran demasiados
        // mensajes sobre la pantalla a la vez... deja solo los de los
        // ligues") — liguesWin/handEndWinner are the two events that fire
        // during the ligues/points-counting phase, each seat in turn; this
        // banner was firing there TOO, on top of the per-player toast each
        // already gets (scorePoints/liguesWin's own text, handEndWinner's
        // Las31/El Punto text) — up to 4 seats' worth stacking up on
        // screen at once. Offline never had an equivalent center banner for
        // this phase either (just the per-player toasts), so this is a
        // straight removal for these two kinds, not a reformat.
        if (pointsAwarded > 0 && awardedTeam && kind !== 'liguesWin' && kind !== 'handEndWinner') {
            toastManagerRef.current?.showToast(
                `${TOAST_ICONS.TEAM_SCORE} ${formatTeam(awardedTeam)}${SEPARATOR}${formatPoints(pointsAwarded)}`,
                { type: 'team' + awardedTeam, teamColor: awardedTeamColor, duration: 3000, position: 'center' }
            );
        }
    }, [t, visualSlotFor, teamColors, toHeaderPlayer, showLas31ScoreRecap, team1Marker, team2Marker, spectator]);

    /**
     * TODO.md (Báciga online Las31: cards not showing / turns stepping on
     * each other) — most of a Las31 turn now reveals immediately, straight
     * off its own event (see knownLas31CardsBySeatRef's doc comment): a
     * 'cardToHide'/uncovered 'dealt31'/'coveredCardRevealed' already carries
     * everything needed, no sync required. Only two things genuinely can't
     * be known from the event alone and still route through
     * pendingLas31RevealSeatsRef/pendingSeatEventsRef here: (1) MY OWN new
     * card dealt covered (the event can't safely carry it — see 'dealt31'
     * in processActionEvent), and (2) the round-end score-announcement
     * subphase, where endLas31() silently uncovers everyone with no event
     * of its own, and bacigaEngine.ts's own architecture can still chain
     * MULTIPLE seats' whole 'scoreAnnounced' sequences into one burst before
     * the one sync that reveals any of them. handleActionEvent buffers
     * (pendingSeatEventsRef, a FIFO no longer filtered by seat — see its own
     * doc comment) anything that arrives while either case is outstanding;
     * this replays that queue in original order once `payload` (the sync
     * that just landed) can resolve it.
     *
     * A replayed event can itself re-pend the very seat just finished
     * revealing (e.g. the round-end reveal chains straight into the next
     * seat's own 'scoreAnnounced') — the outer loop simply picks that back
     * up and repeats, so nothing is left stranded with no future sync ever
     * expected to resume it.
     */
    const drainLas31Reveals = useCallback(async (payload) => {
        for (;;) {
            if (pendingLas31RevealSeatsRef.current.size === 0) {
                if (pendingSeatEventsRef.current.length === 0) return;
                // Nobody's reveal is due yet — this is a fresh seat's own
                // 'cardToHide' (or some other event needing no reveal at
                // all, e.g. a plain 'vote'). Replaying it may itself pend a
                // seat, which the next loop iteration picks up.
                const [entry] = pendingSeatEventsRef.current.splice(0, 1);
                await processActionEvent(entry);
                continue;
            }

            const [seat] = pendingLas31RevealSeatsRef.current;
            pendingLas31RevealSeatsRef.current.delete(seat);

            while (pendingSeatEventsRef.current[0]?.seat === seat) {
                const [entry] = pendingSeatEventsRef.current.splice(0, 1);
                await processActionEvent(entry);
            }
            if (pendingLas31RevealSeatsRef.current.has(seat)) continue; // one of those replays queued up more reveal work for the same seat (another hit) — finish that before revealing

            const coveredIndex = coveredIndexBySeatRef.current[seat];
            const aligned = !spectator && seat === mySeatRef.current
                ? payload.myHand
                : alignLas31Cards(payload.otherHands[seat]?.count ?? 0, coveredIndex, payload.otherHands[seat]?.cards, true);
            knownLas31CardsBySeatRef.current[seat] = aligned;
            await rendererRef.current?.revealSeatCards(seat, aligned, coveredIndex);
            // Offline parity — the running Las31 score recap ("24 y la
            // tapada"...) follows a hit/covered-reveal/Las31 wildcard bind,
            // using the exact same aligned hand just revealed.
            if (pendingLas31ScoreToastSeatsRef.current.has(seat)) {
                pendingLas31ScoreToastSeatsRef.current.delete(seat);
                showLas31ScoreRecap(seat, aligned, coveredIndex);
            }
        }
    }, [processActionEvent, showLas31ScoreRecap, spectator]);

    /**
     * TODO.md (Báciga online dealing pacing / Las31: cards not showing /
     * turns stepping on each other) — a thin wrapper with two buffering
     * gates before an event is actually shown: (1) any event that arrives
     * while a mid-round redeal is pending (bacigaEngine.ts's
     * endHand()->stepDealing() can chain the new mano's own AI vote/torito
     * into the SAME broadcast burst as the 'handDealt' that announced the
     * new hand, all before the one deferred GAME_STATE_SYNC that actually
     * carries the new hand's cards) gets buffered instead of shown
     * immediately — otherwise the human would see "Fulano: Paso" over an
     * already-cleared table, before ever watching the new hand get dealt.
     * handleGameStateSync's own pendingHandRedealRef branch replays this
     * buffer, in order, right after dealInitial() finishes.
     *
     * (2) Similarly, gated on "is ANY seat's reveal still outstanding" (not
     * just this event's own seat) — now a narrow case rather than most of
     * Las31 (see knownLas31CardsBySeatRef/pendingLas31RevealSeatsRef's own
     * doc comments for what still needs it: my own covered hit, the
     * round-end reveal). The "any seat" gate — not just this event's own —
     * still matters for that case: bacigaEngine.ts can chain the round-end
     * score-announcement subphase across MULTIPLE seats in one burst before
     * the one sync that reveals any of them, and a per-seat gate would let a
     * LATER seat's own 'scoreAnnounced' straight through while an earlier
     * one was still waiting on that same sync — the exact "turns
     * overlapping" symptom this was written to fix. Drained by
     * drainLas31Reveals() (see its own doc comment) once that sync lands.
     */
    const handleActionEvent = useCallback((payload) => enqueue(async () => {
        if (pendingHandRedealRef.current) {
            pendingRedealEventsRef.current.push(payload);
            return;
        }
        if (pendingLas31RevealSeatsRef.current.size > 0) {
            pendingSeatEventsRef.current.push(payload);
            return;
        }
        await processActionEvent(payload);
    }), [enqueue, processActionEvent]);

    const handleSeatEvent = useCallback((seat, status) => {
        rendererRef.current?.setSeatDimmed(seat, status !== 'reconnected');
        const key =
            status === 'disconnected'
                ? 'online.play.seat_disconnected'
                : status === 'substituted_inactivity'
                  ? 'online.play.seat_substituted_inactivity'
                  : status === 'substituted'
                    ? 'online.play.seat_substituted'
                    : 'online.play.seat_reconnected';
        toastManagerRef.current?.showPlayerToast(t(key), visualSlotFor(seat), {
            type: status === 'reconnected' ? 'success' : 'warning',
            duration: 3000,
        });
    }, [t, visualSlotFor]);

    /**
     * A round win, match not over yet (matchTransition.ts) — see
     * OnlineRondaScreen.jsx's identical handler for the full explanation.
     *
     * TODO.md ("un jugador de la IA se hizo las cosas... el juego se me
     * bloqueó") — expectingTransitionCloseRef/roundTransitionRef used to be
     * set INSIDE the enqueue()'d callback, right alongside the modal's own
     * setRoundTransition. That's fine when nothing else is backlogged, but
     * a hand that ends via a long chained cascade (Las31 turns for every
     * seat, each reveal paced for the eye) can take longer to drain through
     * enqueue() than matchTransition.ts's own ackDeadlineMs (10s) fallback
     * — the server closes THIS socket for the transition (ack or timeout,
     * whichever comes first) while the client is still busy animating
     * everything that led up to it, so the close arrives before this
     * handler ever runs and expectingTransitionCloseRef is still false.
     * onConnectionChange then can't tell that close apart from a real
     * disconnect, GameWsClient auto-reconnects into the new round on its
     * own (correctly resyncing the board), and by the time enqueue()
     * finally reaches this handler, it opens the round-transition modal
     * for a transition that already fully happened — a modal now stuck
     * forever, since nothing will ever again call onRoundTransition() to
     * close it. Setting both refs synchronously, the instant the message
     * arrives (before enqueue(), not inside it), guarantees they're true
     * before any close this message could possibly cause, no matter how
     * backlogged the animation queue is — only the user-visible modal
     * itself stays queued, so it still waits its turn instead of
     * interrupting whatever's mid-animation.
     */
    const handleRoundTransitionEvent = useCallback((payload) => {
        expectingTransitionCloseRef.current = true;
        roundTransitionRef.current = payload;
        return enqueue(async () => {
            if (unmountedRef.current) return;
            // TODO.md ("despista cuando no sabes que ha pasado... es tan
            // rapido que el jugador no sabe que ha pasado") — the last
            // scoring toast (handEndWinner/lasCosas/etc., 2200ms) was still
            // fading in when this modal used to slam over it the instant it
            // reached the front of the queue. A beat here gives the eye time
            // to actually read what just happened before the round-won
            // screen takes over.
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.navigation.beforeOnlineBacigaWinnerModal));
            if (unmountedRef.current) return;
            setHasAckedTransition(false);
            setRoundTransition(payload);
            if (payload.matchScore) setMatchScore({ team1: payload.matchScore[1], team2: payload.matchScore[2] });
        });
    }, [enqueue]);

    const handleMatchOverEvent = useCallback((payload) => {
        // Same reasoning as handleRoundTransitionEvent above.
        expectingTransitionCloseRef.current = true;
        return enqueue(async () => {
            if (unmountedRef.current) return;
            // Same read-the-last-toast pause as handleRoundTransitionEvent.
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.navigation.beforeOnlineBacigaWinnerModal));
            if (unmountedRef.current) return;
            wsClientRef.current?.close();
            setMatchOver(payload);
            if (payload.matchScore) setMatchScore({ team1: payload.matchScore[1], team2: payload.matchScore[2] });
        });
    }, [enqueue]);

    const handleRoundTransitionContinue = useCallback(() => {
        setHasAckedTransition(true);
        wsClientRef.current?.send({ type: 'ROUND_CONTINUE_ACK' });
    }, []);

    const handlePhase31SummaryContinue = useCallback(() => {
        setPhase31SummaryData(null);
        phase31SummaryContinueResolveRef.current?.();
        phase31SummaryContinueResolveRef.current = null;
    }, []);

    // Spectators have no chat (they aren't in the room) — keeps the hook from
    // even trying the room's chat endpoint.
    const chat = useRoomChat({ roomId, authFetch, wsClient, userId: user?.id, enabled: !spectator });
    const chatToggle = useChatUnread(roomId, chat.messages);

    /**
     * TODO.md idea: float a quick-reaction emoji over the sending player's
     * seat when it arrives in chat. `null` until the first message list is
     * seen, so the initial REST backfill (waiting-room history, or a
     * reconnect's) never replays old reactions — only messages that show up
     * AFTER that point are new arrivals worth animating.
     */
    const seenChatMessageIdsRef = useRef(null);
    useEffect(() => {
        if (seenChatMessageIdsRef.current === null) {
            seenChatMessageIdsRef.current = new Set(chat.messages.map((m) => m.id));
            return;
        }
        for (const message of chat.messages) {
            if (seenChatMessageIdsRef.current.has(message.id)) continue;
            seenChatMessageIdsRef.current.add(message.id);
            if (message.kind === 'emote' && message.senderSeat !== null && message.senderSeat !== undefined) {
                const emoji = EMOTE_PRESETS.find((e) => e.slug === message.body)?.emoji;
                if (emoji) toastManagerRef.current?.showPlayerReaction(emoji, visualSlotFor(message.senderSeat));
            }
        }
    }, [chat.messages, visualSlotFor]);

    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            wsClientRef.current?.close();
            toastManagerRef.current?.destroy();
            rendererRef.current?.destroy();
        };
    }, []);

    const handlePixiMount = useCallback(async (app) => {
        appRef.current = app;
        toastManagerRef.current = new ToastManager(app);
        soundVibrationService.initialize();

        try {
            // A spectator isn't in the room, so /rooms/mine has nothing for
            // them — /rooms/live/:id (public, in-progress rooms only) gives
            // the same seats/match score without any hand information.
            const room = await authFetch(spectator ? `/rooms/live/${roomId}` : '/rooms/mine');
            if (unmountedRef.current) return;
            if (room.id !== roomId) {
                navigate('/online', { replace: true });
                return;
            }
            playersBySeatRef.current = [0, 1, 2, 3].map((seat) => {
                const seatInfo = room.seats.find((s) => s.seat === seat);
                if (seatInfo?.occupantType === 'HUMAN') return { name: seatInfo.userName || '?', isSystem: false };
                if (seatInfo?.occupantType === 'SYSTEM') {
                    const persona = getAiPersona(room.id, seat);
                    return { name: `${persona.name} ${t('online.play.ai_name_suffix')}`, avatar: persona.avatar, isSystem: true };
                }
                return { name: '', isSystem: false };
            });
            setMatchScore({ team1: room.matchScoreTeam1 || 0, team2: room.matchScoreTeam2 || 0 });
        } catch {
            if (!unmountedRef.current) navigate('/online', { replace: true });
            return;
        }

        const client = new GameWsClient(roomId, getAccessToken, { spectate: spectator });
        wsClientRef.current = client;
        setWsClient(client);
        // The game ended, was cancelled, or can't be watched any more (the
        // server closes with a terminal code and a retry would never succeed).
        client.onTerminalClose = () => {
            if (!unmountedRef.current) navigate('/online', { replace: true });
        };
        client.onConnectionChange = (isConnected) => {
            if (unmountedRef.current) return;
            if (!isConnected && expectingTransitionCloseRef.current) {
                // The server closed this on purpose — see
                // OnlineRondaScreen.jsx's identical handling for the full
                // explanation.
                client.close();
                if (roundTransitionRef.current) onRoundTransition?.(roundTransitionRef.current.nextGameType);
                return;
            }
            setConnectionStatus(isConnected ? 'connected' : 'reconnecting');
            if (isConnected) {
                setReconnectAttempt(0);
                if (hasDealtRef.current) pendingResyncRef.current = true;
            }
        };
        client.onReconnecting = (attempt) => {
            if (!unmountedRef.current) setReconnectAttempt(attempt);
        };
        client.on('GAME_STATE_SYNC', (msg) => handleGameStateSync(msg.payload, msg.isReconnect));
        client.on('BACIGA_ACTION_EVENT', (msg) => handleActionEvent(msg.payload));
        client.on('ROUND_TRANSITION', (msg) => handleRoundTransitionEvent(msg.payload));
        client.on('MATCH_OVER', (msg) => handleMatchOverEvent(msg.payload));
        client.on('PLAYER_DISCONNECTED', (msg) => handleSeatEvent(msg.payload.seat, 'disconnected'));
        client.on('PLAYER_RECONNECTED', (msg) => handleSeatEvent(msg.payload.seat, 'reconnected'));
        client.on('PLAYER_SUBSTITUTED', (msg) =>
            handleSeatEvent(msg.payload.seat, msg.payload.reason === 'inactivity' ? 'substituted_inactivity' : 'substituted')
        );
        client.on('GAME_CANCELLED', (msg) => setFatalError(msg.payload?.reason || t('online.play.game_cancelled')));
        client.on('ERROR', (msg) => {
            toastManagerRef.current?.showToast(msg.message || t('online.errors.generic'), { type: 'error', duration: 3000 });
            // A rejected action has no follow-up GAME_STATE_SYNC of its own
            // — without this, sendAction's proactive
            // setProcessingDecision(true) would never get undone, leaving
            // BacigaDecisionPanel permanently disabled.
            client.send({ type: 'REQUEST_STATE' });
        });
        client.connect();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs/callbacks only, deliberately mounted once per screen instance
    }, [roomId, spectator]);

    const handleBackToLobby = useCallback(() => {
        wsClientRef.current?.close();
        navigate('/online');
    }, [navigate]);

    const sendAction = useCallback((type, payload) => {
        setProcessingDecision(true);
        wsClientRef.current?.send({ type, actionId: crypto.randomUUID(), payload });
    }, []);

    const handleTorito = useCallback((wantsTorito) => sendAction('BACIGA_TORITO_DECISION', { wantsTorito }), [sendAction]);
    const handleVote = useCallback((decision, discardIndices) => {
        // Remembered here, not read back from the server: bacigaEngine.ts
        // stages a 'paso' vote's discardIndices privately (pendingDiscards)
        // until every seat has voted, then deals replacements in one silent
        // batch (see 'cardsPassed' handling in handleActionEvent below) — my
        // own indices never come back on the wire, so this is the only place
        // that ever knows them.
        if (decision === 'paso' && discardIndices?.length > 0) {
            myDiscardIndicesRef.current = discardIndices;
        }
        sendAction('BACIGA_VOTE', { decision, discardIndices });
    }, [sendAction]);
    // Offline parity (Baciga.jsx's handleToggleCardSelection) — BacigaDecisionPanel calls this the instant a card is picked/unpicked for discard, well before "Confirmar Paso" is sent; flips (or un-flips) that one hand sprite in place. Not a sendAction — purely local/visual, nothing to tell the server yet.
    const handlePreviewDiscard = useCallback((index, selected) => {
        if (selected) rendererRef.current?.previewDiscard(index);
        else rendererRef.current?.revertDiscardPreview(index);
    }, []);
    const handleCounterDeclare = useCallback((decision) => sendAction('BACIGA_COUNTER_DECLARE', { decision }), [sendAction]);
    const handleSelectCardToHide = useCallback((cardIndex) => sendAction('BACIGA_SELECT_CARD_TO_HIDE', { cardIndex }), [sendAction]);
    const handleHitOrStand = useCallback((action) => sendAction('BACIGA_HIT_OR_STAND', { action }), [sendAction]);
    const handleRevealCovered = useCallback(() => sendAction('BACIGA_REVEAL_COVERED_CARD', {}), [sendAction]);

    // Deliberately NOT local state for the FORCED case: `open` is derived
    // straight from `pending` on every render, so there's nothing that could
    // ever drift out of sync with the server. This matters because — unlike
    // offline's synchronous handleWildcardSelect — binding here is a real
    // network round-trip: WildcardSelector's own handleSelect calls
    // onSelect() then immediately onClose() in the same tick, well before the
    // server confirms the bind via the next GAME_STATE_SYNC. If `open` were
    // separate state closed by that onClose call, it would already read as
    // "closed" while `pending` still said 'bindWildcard' — then the dialog
    // would just stay closed once `pending` legitimately changed (nothing
    // left to react to). With `open` computed straight from `pending`, the
    // dialog closes itself the instant the server actually confirms,
    // automatically, every time.
    const bindWildcardPending = pending?.type === 'bindWildcard';
    // Offline parity (Baciga.jsx's "🎴 Amarrar Comodín" button, hasUnboundWildcard):
    // a player can VOLUNTARILY bind a wildcard ahead of time, any time it's
    // otherwise their own pending decision (vote/counterDeclare/las31Action/
    // selectCardToHide) — bacigaEngine.ts's bindWildcard() already supports
    // this server-side (its isVoluntaryStart branch), online's UI just never
    // offered it. `pending` is already privacy-filtered to null unless it's
    // genuinely this seat's turn (see BacigaDecisionPanel's doc comment), so
    // "pending exists and isn't already the forced bind" is enough — no need
    // to also compare pending.seat against my own seat.
    const hasUnboundWildcard = myHand.some((c) => bacigaLogic.isComodin(c) && !bacigaLogic.isCardBound(c));
    const canVoluntarilyBindWildcard = Boolean(pending) && !bindWildcardPending && hasUnboundWildcard;
    // Unlike the forced case, a voluntary bind doesn't change `pending` once
    // it completes (the seat still owes its original vote/las31Action/etc.),
    // so there's nothing to self-heal from — voluntaryWildcardCardIndex has
    // to be cleared explicitly on both a successful bind and a genuine cancel.
    const handleOpenWildcardSelector = useCallback(() => {
        const idx = myHand.findIndex((c) => bacigaLogic.isComodin(c) && !bacigaLogic.isCardBound(c));
        if (idx !== -1) setVoluntaryWildcardCardIndex(idx);
    }, [myHand]);
    const wildcardCardIndex = bindWildcardPending ? pending.cardIndex : voluntaryWildcardCardIndex;
    const wildcardSelectorOpen = bindWildcardPending || voluntaryWildcardCardIndex !== null;
    const wildcardCard = wildcardSelectorOpen ? (myHand[wildcardCardIndex] ?? null) : null;
    const handleBindWildcard = useCallback((value) => {
        if (wildcardCardIndex === null || wildcardCardIndex === undefined) return;
        setVoluntaryWildcardCardIndex(null);
        sendAction('BACIGA_BIND_WILDCARD', { cardIndex: wildcardCardIndex, value });
    }, [sendAction, wildcardCardIndex]);

    // CSS styles for rotation fallback when the native/Web Orientation lock fails (web only)
    const rotationWrapperStyle = needsRotation ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vh',
        height: '100vw',
        transform: 'rotate(90deg)',
        transformOrigin: 'top left',
        marginLeft: '100vw',
        overflow: 'hidden',
    } : {};

    return (
        <Box sx={rotationWrapperStyle}>
            <Header
                title={t('online.play.title_with_game', { game: t('online.lobby.game_type_baciga') })}
                showSettings={false}
                onBack={handleBackToLobby}
                globalScore={matchScore}
                players={playersBySeatRef.current}
                dealer={toHeaderPlayer(dealerIndexRef.current)}
                player={toHeaderPlayer(currentTurnRef.current)}
                onToggleChat={spectator ? undefined : chatToggle.toggle}
                chatUnreadCount={chatToggle.unreadCount}
                onOnlineSettings={spectator ? undefined : () => setOnlineSettingsOpen(true)}
            />

            {spectator && (
                <Alert severity="info" icon={<VisibilityIcon fontSize="inherit" />} sx={{ position: 'fixed', top: 56, left: 0, right: 0, zIndex: 1900, py: 0 }}>
                    {t('online.spectate.banner')}
                </Alert>
            )}

            <OnlineSettingsModal
                open={onlineSettingsOpen}
                onClose={() => setOnlineSettingsOpen(false)}
                gameType="BACIGA"
                getGameSnapshot={() => ({
                    roomId,
                    connectionStatus,
                    scores,
                    matchScore,
                    dealerIndex: dealerIndexRef.current,
                    currentTurn: currentTurnRef.current,
                    mySeat: mySeatRef.current,
                })}
            />

            {connectionStatus === 'reconnecting' && (
                <Alert severity="warning" sx={{ position: 'fixed', top: 56, left: 0, right: 0, zIndex: 2000 }}>
                    {reconnectAttempt > 0
                        ? t('online.play.reconnecting_attempt', { attempt: reconnectAttempt })
                        : t('online.play.reconnecting')}
                </Alert>
            )}

            <Box sx={{
                width: needsRotation ? '100vh' : '100%',
                height: needsRotation ? 'calc(100vw - 56px)' : 'calc(100dvh - 56px)',
            }}>
                <GameTable onMount={handlePixiMount} backgroundColor={0x257241} />
            </Box>

            <BacigaDecisionPanel
                pending={pending}
                myHand={myHand}
                hasCoveredCard={hasCoveredCard}
                disabled={processingDecision}
                isMobile={isMobile}
                onTorito={handleTorito}
                onVote={handleVote}
                onCounterDeclare={handleCounterDeclare}
                onSelectCardToHide={handleSelectCardToHide}
                onHitOrStand={handleHitOrStand}
                onRevealCovered={handleRevealCovered}
                onPreviewDiscard={handlePreviewDiscard}
            />

            {canVoluntarilyBindWildcard && (
                <Button
                    variant="contained"
                    sx={{
                        position: 'fixed',
                        bottom: 100,
                        right: 20,
                        color: '#000 !important',
                        background: '#FFD700 !important',
                        backgroundColor: '#FFD700 !important',
                        border: '2px solid #FFD700',
                        '&:hover': {
                            background: '#FFA500 !important',
                            backgroundColor: '#FFA500 !important',
                            border: '2px solid #FF8C00',
                            transform: 'scale(1.05)',
                        },
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        padding: '10px 20px',
                        boxShadow: '0 4px 8px rgba(255, 215, 0, 0.5)',
                        zIndex: 1000,
                    }}
                    onClick={handleOpenWildcardSelector}
                >
                    {t('online.play.baciga.bind_wildcard_button')}
                </Button>
            )}

            <WildcardSelector
                open={wildcardSelectorOpen}
                onClose={() => {
                    if (bindWildcardPending) {
                        // Not a real cancel (there's no valid "skip" here —
                        // the engine stays blocked until a value is bound):
                        // either a genuine attempt to dismiss without
                        // choosing, or the tail end of WildcardSelector's own
                        // onSelect->onClose sequence a beat before the
                        // server's confirming sync lands — either way,
                        // nothing to do but explain.
                        toastManagerRef.current?.showToast(t('online.play.baciga.must_bind_wildcard'), { type: 'warning', duration: 2000 });
                        return;
                    }
                    // Voluntary bind: a real cancel is fine, same as offline
                    // (Baciga.jsx's own onClose only warns for its forced
                    // cases) — also reached, harmlessly, right after picking
                    // a value (handleBindWildcard already cleared this).
                    setVoluntaryWildcardCardIndex(null);
                }}
                onSelect={handleBindWildcard}
                card={wildcardCard}
                currentValue={wildcardCard?.assignedValue ?? null}
                playerCards={myHand}
            />

            {!spectator && (
                <ChatPanel
                    variant="floating"
                    messages={chat.messages}
                    onSend={chat.sendMessage}
                    myUserId={user?.id}
                    disabled={chat.isSending}
                    open={chatToggle.expanded}
                    onClose={chatToggle.toggle}
                />
            )}

            <ShufflingModal
                open={Boolean(shufflingDealer)}
                playerName={shufflingDealer?.name}
                playerAvatar={shufflingDealer?.avatar}
            />

            <Phase31SummaryModal
                open={Boolean(phase31SummaryData)}
                summaryData={phase31SummaryData}
                players={playersBySeatRef.current}
                onContinue={handlePhase31SummaryContinue}
            />

            <RoundTransitionModal
                open={Boolean(roundTransition)}
                winnerTeamId={roundTransition?.winningTeam}
                matchScore={roundTransition?.matchScore}
                nextGameName={
                    roundTransition?.nextGameType === 'BACIGA' ? t('online.lobby.game_type_baciga') : t('online.lobby.game_type_ronda')
                }
                playersBySeat={playersBySeatRef.current}
                hasAcked={hasAckedTransition}
                ackDeadlineAt={roundTransition?.ackDeadlineAt}
                onContinue={handleRoundTransitionContinue}
                readOnly={spectator}
            />

            <MatchWinnerModal
                open={Boolean(matchOver)}
                winnerTeamId={matchOver?.winningTeam}
                playersBySeat={playersBySeatRef.current}
                onBackToLobby={handleBackToLobby}
            />

            <Dialog open={Boolean(fatalError)}>
                <DialogTitle>{t('online.play.game_cancelled')}</DialogTitle>
                <DialogContent>
                    <Typography>{fatalError}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleBackToLobby}>
                        {t('online.lobby.title')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
